import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AlertTriangle, Wifi, WifiOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Live data connectivity banner.
 *
 * Fetches /api/deal-scanner/status and shows one of two states:
 *   1. "Live Property Data: Not Connected" (with sample-data notice)
 *   2. "Live Property Data: Connected" (source label from backend)
 *
 * We NEVER present sample/manual data as if it were a real listing.
 */
export default function SampleDataBanner({ label }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let active = true;
    axios
      .get(`${BACKEND_URL}/api/deal-scanner/status`, { withCredentials: true })
      .then((res) => { if (active) setStatus(res.data); })
      .catch(() => { if (active) setStatus({ connected: false, provider_id: 'demo', is_live: false }); });
    return () => { active = false; };
  }, []);

  const isLive = !!status?.is_live;
  const providerLabel = label || status?.source_label || t('dealScanner.sampleBadge');

  if (isLive) {
    return (
      <div
        data-testid="scanner-live-data-banner"
        className="rounded-xl border border-emerald-300/70 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-900 dark:text-emerald-200 px-4 py-3 flex items-start gap-3"
      >
        <Wifi className="w-5 h-5 mt-0.5 shrink-0" />
        <div className="text-sm leading-relaxed">
          <span className="font-semibold uppercase tracking-wide text-xs mr-2">
            {t('dealScanner.liveConnectedBadge')}
          </span>
          {t('dealScanner.liveConnectedNotice', { source: providerLabel })}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="scanner-sample-data-banner"
      className="rounded-xl border border-amber-300/70 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 text-amber-900 dark:text-amber-200 px-4 py-3 flex items-start gap-3"
    >
      <div className="mt-0.5 shrink-0 relative">
        <AlertTriangle className="w-5 h-5" />
        <WifiOff className="w-3 h-3 absolute -bottom-1 -right-1 text-amber-700 dark:text-amber-300" />
      </div>
      <div className="text-sm leading-relaxed">
        <div className="flex items-center flex-wrap gap-x-2">
          <span
            data-testid="scanner-experimental-tag"
            className="text-[10px] uppercase tracking-wider font-extrabold bg-amber-500/30 dark:bg-amber-500/40 text-amber-900 dark:text-amber-100 px-2 py-0.5 rounded-full"
          >
            {t('dealScanner.experimentalTag')}
          </span>
          <span className="font-semibold uppercase tracking-wide text-xs">
            {providerLabel}
          </span>
          <span
            data-testid="scanner-live-not-connected-tag"
            className="text-[10px] uppercase tracking-wider font-bold bg-amber-500/20 dark:bg-amber-500/30 px-2 py-0.5 rounded-full"
          >
            {t('dealScanner.liveNotConnectedTag')}
          </span>
        </div>
        <p className="mt-0.5 font-medium">{t('dealScanner.experimentalNotice')}</p>
        <p className="mt-0.5 text-xs opacity-80">{t('dealScanner.liveNotConnectedNotice')}</p>
      </div>
    </div>
  );
}
